import type { ImageRef } from '../wiki/types';

export type TileKind = 'word' | 'heading' | 'title' | 'image' | 'wall' | 'door' | 'ledge' | 'label';

/** Tile flag bits. Physics reads SOLID/ONEWAY/TIMED/SPRING/ICE/HAZARD; the rest drive gameplay & rendering. */
export const F = {
  SOLID: 1,
  ONEWAY: 2,
  TIMED: 4,
  SPRING: 8,
  ICE: 16,
  HAZARD: 32,
  LINK: 64,
  RED: 128,
  EXIT: 256,
  VISITED: 512,
  HOT: 1024,
  WARM: 2048,
  FOUND: 4096,
} as const;

export type Door =
  | { kind: 'toc'; section: number }
  | { kind: 'contents' }
  | { kind: 'top' }
  | { kind: 'back' };

export interface Tile {
  id: number;
  kind: TileKind;
  x: number;
  y: number;
  w: number;
  h: number;
  flags: number;
  text?: string;
  font?: string;
  /** Baseline offset from the tile's top edge. */
  base?: number;
  color?: string;
  link?: string;
  occ?: number;
  door?: Door;
  image?: ImageRef;
  /** Timed tiles: phase offset in [0, 1). */
  phase?: number;
  section: number;
}

export interface Occurrence {
  id: number;
  title: string;
  tiles: number[];
  section: number;
  text: string;
  x: number;
  y: number;
}

export interface LinkInfo {
  title: string;
  redirect: boolean;
  occs: number[];
  /** EXIT / HOT / WARM / VISITED bits, mirrored onto tiles. */
  flags: number;
}

export interface Coin {
  x: number;
  y: number;
  label: string;
}

export interface EnemySpawn {
  x: number;
  y: number;
  kind: 'cn' | 'vandal';
}

export interface Section {
  title: string;
  level: number;
  y: number;
  spawn: { x: number; y: number };
}

export interface Decor {
  x: number;
  y: number;
  text: string;
  font: string;
  color: string;
  align?: 'left' | 'center' | 'right';
}

export interface Panel {
  x: number;
  y: number;
  w: number;
  h: number;
  kind: 'infobox' | 'toc' | 'quote' | 'header';
}

export interface Level {
  title: string;
  displayTitle: string;
  description: string;
  lang: string;
  width: number;
  height: number;
  tiles: Tile[];
  links: Map<string, LinkInfo>;
  occs: Occurrence[];
  coins: Coin[];
  enemies: EnemySpawn[];
  sections: Section[];
  spawn: { x: number; y: number };
  tocSpawn?: { x: number; y: number };
  ladder: { x: number; w: number; y0: number; y1: number };
  marks: { x: number; y: number; kind: 'para' | 'heading' }[];
  rules: { x: number; y: number; w: number }[];
  panels: Panel[];
  decor: Decor[];
  words: number;
}
